import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import Field from '../../components/common/Field';
import Spinner from '../../components/common/Spinner';
import ImageGalleryPicker from '../../components/common/ImageGalleryPicker';
import eventsService from '../../api/services/events.service';
import imagesService from '../../api/services/images.service';
import signupsService from '../../api/services/signups.service';
import donationMethodsService from '../../api/services/donationMethods.service';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import { nextSlotDateId, nextSlotWinId, nextSignupItemId, spotsLabel, slotSubLabel } from '../../store/useSignupsStore';
import { showToast } from '../../store/useToastStore';
import { fmtDate } from '../../utils/formatters';
import { HOUR_OPTIONS, MINUTE_OPTIONS, splitTime, joinTime, isAllDay } from '../../utils/time';
import { PATHS } from '../../routes/navConfig';

/* Sign-ups hang off a calendar event, so pull enough of them to cover a term
   in one request rather than paging inside a dropdown. */
const EVENT_FETCH_LIMIT = 100;

/* One kind per sign-up, picked first. A class that needs both volunteers and
   supplies makes two. */
const KINDS = [
  {
    kind: 'slots',
    title: 'Time slots',
    sub: 'Parents pick a window that works for them.',
    icon: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></svg>,
  },
  {
    kind: 'open',
    title: 'By date',
    sub: 'Parents pick which date they take. No time windows.',
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M16 19v-1.8a3 3 0 0 0-3-3H6.5a3 3 0 0 0-3 3V19" />
        <circle cx="9.7" cy="7.2" r="3.2" />
        <path d="M20.5 19v-1.8a3 3 0 0 0-2.3-2.9M15.8 4.2a3.2 3.2 0 0 1 0 6" />
      </svg>
    ),
  },
  {
    kind: 'items',
    title: 'Supplies',
    sub: 'Parents claim items off a list.',
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M5.2 7.5h13.6l-1.1 11.6a2 2 0 0 1-2 1.8H8.3a2 2 0 0 1-2-1.8Z" />
        <path d="M9.2 7.5V5.8a2.8 2.8 0 0 1 5.6 0v1.7" />
      </svg>
    ),
  },
];

const KIND_SUB = {
  slots: 'Add dates and time windows — volunteers pick whichever works for them.',
  open: 'Add the dates. Parents pick one to take — a week, a day, a turn.',
  items: 'List what you need. Parents claim items off the list.',
};

const newWindow = (start = '09:00', end = '10:00') => ({ id: nextSlotWinId(), start, end, anyTime: false, capacity: 2 });
/* a By-date turn is one family by default — a rota, not a shift */
const newOpenWindow = (capacity = 1) => ({ id: nextSlotWinId(), start: null, end: null, anyTime: true, capacity });
const newDateBlock = (kind) => ({
  id: nextSlotDateId(),
  date: '',
  label: '',
  windows: [kind === 'open' ? newOpenWindow() : newWindow()],
});
const newItem = () => ({ id: nextSignupItemId(), name: '', qty: 1 });

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/* Create/edit a sign-up: kind picker, that kind's editor, and a live preview.
   Both Add and Edit fetch the network's events fresh on open — the dropdown
   always shows what the calendar actually holds right now. */
export default function SignupForm({ editingSignup, onCancel, onSaved }) {
  const ctx = useContextStore(selectCurrentContext);
  const navigate = useNavigate();
  /* seed contexts (before /myProfile lands) carry no schoolId — no network, no API */
  const networkId = ctx?.schoolId ? ctx.id : null;

  const initialKind = editingSignup?.kind || 'slots';
  const [kind, setKindState] = useState(initialKind);
  const [title, setTitle] = useState(editingSignup?.title || '');
  const [desc, setDesc] = useState(editingSignup?.desc || '');
  const [acceptsDonations, setAcceptsDonations] = useState(!!editingSignup?.acceptsDonations);
  const [image, setImage] = useState(editingSignup?.image || null);
  /* set only when the photo was uploaded from disk — gallery picks stay URLs */
  const [imageFile, setImageFile] = useState(null);
  const [linkedEventId, setLinkedEventId] = useState(editingSignup?.eventId || '');
  /* dates and items are held separately, so both survive a round trip through
     the other kind before saving */
  const [dates, setDates] = useState(() =>
    editingSignup?.dates?.length
      ? editingSignup.dates.map((d) => ({
          id: d.id,
          date: d.date,
          label: d.label || '',
          windows: d.windows.map((w) => ({ id: w.id, start: w.start, end: w.end, anyTime: !!w.anyTime, capacity: w.capacity })),
        }))
      : [newDateBlock(initialKind)]
  );
  const [items, setItems] = useState(() =>
    editingSignup?.items?.length ? editingSignup.items.map((i) => ({ id: i.id, name: i.name, qty: i.qty })) : [newItem()]
  );
  const [repeatEvery, setRepeatEvery] = useState('7');
  const [repeatCount, setRepeatCount] = useState('6');
  const [saving, setSaving] = useState(false);

  /* ── events: always live, never the store's cached/seed list ── */
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  useEffect(() => {
    if (!networkId) {
      setEvents([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setEventsLoading(true);
      try {
        const r = await eventsService.listEvents(networkId, { page: 1, limit: EVENT_FETCH_LIMIT });
        if (!cancelled) setEvents(r.events);
      } catch (err) {
        if (!cancelled) {
          setEvents([]);
          showToast(err.message);
        }
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [networkId]);

  /* The Donate button reuses the class's donation links — warn when there are
     none. null = not known (no network, or the lookup failed): say nothing. */
  const [donationLinkCount, setDonationLinkCount] = useState(null);
  useEffect(() => {
    if (!networkId) return undefined;
    let cancelled = false;
    donationMethodsService
      .list(networkId, { page: 1, limit: 1 })
      .then((r) => {
        if (!cancelled) setDonationLinkCount(r.total);
      })
      .catch(() => {
        if (!cancelled) setDonationLinkCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [networkId]);

  const sortedEvents = [...events].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  /* an edited sign-up can point at an event that has since been deleted or has
     paged out — keep the selection legible instead of silently blanking it */
  const linkedMissing = !!linkedEventId && !eventsLoading && !events.some((e) => e.id === linkedEventId);

  /* Switching kind keeps whatever was typed. Slots → By date keeps each date's
     first window's spots and drops the rest, rather than keeping shifts that can
     no longer be seen; By date → slots fills 9–10 so the pickers are never
     blank, and drops the date labels that editor has no field for. */
  const setKind = (next) => {
    if (next === kind) return;
    if (next === 'open') {
      setDates((prev) =>
        prev.map((d) => {
          const first = d.windows[0] || { id: nextSlotWinId(), capacity: 1 };
          return { ...d, windows: [{ ...first, start: null, end: null, anyTime: true }] };
        })
      );
    } else if (next === 'slots') {
      setDates((prev) =>
        prev.map((d) => ({
          ...d,
          label: '',
          windows: d.windows.map((w) =>
            w.anyTime ? { ...w, anyTime: false, start: w.start || '09:00', end: w.end || '10:00' } : w
          ),
        }))
      );
    }
    setKindState(next);
  };

  /* Picking an event pre-fills the sign-up's title, and on a fresh form its
     first date, times and spots — everything stays editable. The event's photo
     is not pulled in: a sign-up only shows a photo the room parent picked. */
  const applyEventLink = (id) => {
    setLinkedEventId(id);
    if (!id) return;
    const ev = events.find((e) => e.id === id);
    if (!ev) return;
    if (!title.trim()) setTitle(ev.title);
    /* an all-day event's 23:59 isn't a picker option — leave the window's times be */
    const takeTimes = ev.startTime && !isAllDay(ev.startTime, ev.endTime);
    /* the event's Spots carry over, and blank there (no limit) is blank here */
    const evSpots = ev.spots === '' || ev.spots == null ? null : Number(ev.spots) || null;
    if (dates.length && !dates[0].date) {
      setDates((prev) =>
        prev.map((d, i) =>
          i === 0
            ? {
                ...d,
                date: ev.date,
                windows: d.windows.map((w, wi) =>
                  wi === 0
                    ? {
                        ...w,
                        capacity: evSpots,
                        ...(!w.anyTime && takeTimes ? { start: ev.startTime, end: ev.endTime || w.end } : {}),
                      }
                    : w
                ),
              }
            : d
        )
      );
    }
    showToast(`Pulled in details from "${ev.title}" ✓`);
  };

  const patchDate = (dateId, patch) => {
    setDates((prev) => prev.map((d) => (d.id === dateId ? { ...d, ...patch } : d)));
  };

  const patchWindow = (dateId, winId, patch) => {
    setDates((prev) =>
      prev.map((d) =>
        d.id === dateId
          ? { ...d, windows: d.windows.map((w) => (w.id === winId ? { ...w, ...patch } : w)) }
          : d
      )
    );
  };

  const patchItem = (itemId, patch) => {
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, ...patch } : i)));
  };

  /* Weekly rota in one action: the first date that has a value is the anchor,
     the rest are laid out at a fixed interval carrying its spots and label.
     Replaces the list. Local-date arithmetic, so every date keeps its weekday. */
  const generateRepeatDates = () => {
    const anchor = dates.find((d) => d.date);
    if (!anchor) {
      showToast('Set the first date above, then generate the rest.');
      return;
    }
    const step = parseInt(repeatEvery, 10);
    const count = Math.max(2, Math.min(30, parseInt(repeatCount, 10) || 6));
    const [y, m, dd] = anchor.date.split('-').map(Number);
    /* null (no limit) carries over like any other spots value */
    const cap = anchor.windows[0] ? anchor.windows[0].capacity : 1;
    setDates(
      Array.from({ length: count }, (_, i) => ({
        id: nextSlotDateId(),
        date: isoDate(new Date(y, m - 1, dd + i * step)),
        label: anchor.label || '',
        windows: [newOpenWindow(cap)],
      }))
    );
    setRepeatCount(String(count));
    showToast(`${count} dates generated ✓`);
  };

  const handleImageChange = (src, file) => {
    setImage(src);
    setImageFile(file || null);
  };

  /* Photo suggestions come from Unsplash for the typed title, same as events. */
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const generateImages = async () => {
    const q = title.trim();
    if (!q) {
      showToast('Enter a title first, then generate photos.');
      return;
    }
    setSuggestionsLoading(true);
    try {
      setSuggestions(await imagesService.searchUnsplash(q, 8));
    } catch (err) {
      showToast(err.message);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const save = async () => {
    if (!networkId) { showToast('Pick a class first.'); return; }
    if (!title.trim()) { showToast('Give the sign-up a name.'); return; }
    /* the backend ties every sign-up to an event, so this one isn't optional */
    if (!linkedEventId) { showToast('Choose the event this sign-up is for.'); return; }
    let validDates = [];
    let validItems = [];
    if (kind === 'items') {
      validItems = items.filter((i) => i.name.trim()).map((i) => ({ name: i.name.trim(), qty: i.qty }));
      if (!validItems.length) { showToast('Add at least one item.'); return; }
    } else {
      validDates = dates
        .filter((d) => d.date && d.windows.length)
        .map((d) => ({
          date: d.date,
          label: kind === 'open' ? d.label.trim() : '',
          windows: d.windows.map((w) => ({ start: w.start, end: w.end, anyTime: kind === 'open', capacity: w.capacity })),
        }));
      if (!validDates.length) { showToast('Add at least one date.'); return; }
      if (kind === 'slots' && validDates.some((d) => d.windows.some((w) => w.end <= w.start))) {
        showToast('Each time window has to end after it starts.');
        return;
      }
    }

    const fields = {
      eventId: linkedEventId,
      title: title.trim(),
      description: desc.trim(),
      dates: validDates,
      photo: imageFile ? undefined : image || '',
      photoFile: imageFile || undefined,
    };

    setSaving(true);
    try {
      if (editingSignup) {
        const origKind = editingSignup.kind || 'slots';
        await signupsService.update({
          signupId: editingSignup.id,
          ...fields,
          /* the API rejects fields it doesn't know yet, so the new ones only go
             out when this edit changes them */
          kind: kind !== origKind ? kind : undefined,
          items: kind === 'items' ? validItems : origKind === 'items' ? [] : undefined,
          acceptsDonations: acceptsDonations !== !!editingSignup.acceptsDonations ? acceptsDonations : undefined,
        });
        showToast('Sign-up updated ✓');
        onSaved(editingSignup.id, false);
        return;
      }
      await signupsService.create({ networkId, ...fields, kind, items: validItems, acceptsDonations });
      showToast('Sign-up saved — hit Send to parents on the row whenever you\'re ready ✓');
      onSaved(null, true);
    } catch (err) {
      showToast(err.message);
    } finally {
      setSaving(false);
    }
  };

  const previewDates = dates.filter((d) => d.date);
  const previewItems = items.filter((i) => i.name.trim());

  return (
    <>
      <SectionHead
        title={editingSignup ? 'Edit Sign-Up' : 'New Sign-Up'}
        sub={KIND_SUB[kind]}
        actions={<button className="btn btn-ghost" onClick={onCancel}>← Back to Sign-Ups</button>}
      />
      <Card>
        <div className="email-grid">
          <div>
            <Field label="What do you need?" style={{ marginBottom: 16 }}>
              <div className="su-kind-grid" role="radiogroup" aria-label="What do you need?">
                {KINDS.map((k) => (
                  <button
                    key={k.kind}
                    type="button"
                    role="radio"
                    aria-checked={kind === k.kind}
                    className={`su-kind-card ${kind === k.kind ? 'sel' : ''}`}
                    onClick={() => setKind(k.kind)}
                  >
                    {k.icon}
                    <span className="t">{k.title}</span>
                    <span className="d">{k.sub}</span>
                  </button>
                ))}
              </div>
            </Field>

            <Field
              label="Event This Sign-Up Is For"
              style={{ marginBottom: 10 }}
              hint={
                eventsLoading
                  ? 'Loading your latest events…'
                  : sortedEvents.length
                    ? "Pulls in that event's title, date, time, and spots so you're not typing it twice — everything stays editable below."
                    : 'No events on this class calendar yet — add one on the Calendar page first, then come back.'
              }
            >
              <select value={linkedEventId} disabled={eventsLoading} onChange={(e) => applyEventLink(e.target.value)}>
                <option value="">{eventsLoading ? 'Loading events…' : '— Select an event —'}</option>
                {linkedMissing && <option value={linkedEventId}>Linked event (no longer on the calendar)</option>}
                {sortedEvents.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.title}{e.date ? ` · ${fmtDate(e.date)}` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Title" style={{ marginBottom: 10 }}>
              <input
                type="text"
                value={title}
                placeholder="e.g. Fall Field Trip Volunteers"
                onChange={(e) => setTitle(e.target.value)}
              />
            </Field>
            <Field label="Description (optional)" style={{ marginBottom: 16 }}>
              <input
                type="text"
                value={desc}
                placeholder="What will volunteers be doing?"
                onChange={(e) => setDesc(e.target.value)}
              />
            </Field>

            <div className="field" style={{ marginBottom: 18 }}>
              <label className="chk-row" style={{ padding: '10px 0' }}>
                <input type="checkbox" checked={acceptsDonations} onChange={(e) => setAcceptsDonations(e.target.checked)} />
                <div className="body">
                  <div className="t">Also accept donations</div>
                  <div className="d">
                    For parents who can't volunteer time but still want to help — adds a Donate button using your class's
                    donation links.
                  </div>
                </div>
              </label>
              {acceptsDonations && donationLinkCount === 0 && (
                <div className="hint" style={{ marginTop: 6 }}>
                  You haven't set up any{' '}
                  <span className="text-link" onClick={() => navigate(PATHS.DONATION_LINKS)}>donation links</span> yet —
                  add one first so this button has somewhere to send people.
                </div>
              )}
            </div>

            <Field label="Photo (optional)" style={{ marginBottom: 18 }}>
              <ImageGalleryPicker
                image={image}
                onChange={handleImageChange}
                suggestions={suggestions}
                suggestionsLoading={suggestionsLoading}
                onGenerate={generateImages}
              />
            </Field>

            {kind === 'items' ? (
              <Field label="Items Needed">
                <div className="hint" style={{ marginBottom: 8 }}>
                  One row per thing you need. Quantity is how many parents can claim that item — two dozen cupcakes from
                  one parent is quantity 1.
                </div>
                <div>
                  {items.map((it) => (
                    <div className="item-row" key={it.id}>
                      <div className="tp-field">
                        <label>Item</label>
                        <input
                          type="text"
                          value={it.name}
                          placeholder="e.g. Tiny pumpkins"
                          onChange={(ev) => patchItem(it.id, { name: ev.target.value })}
                        />
                      </div>
                      <div className="tp-field qty-field">
                        <label>Qty</label>
                        <input
                          type="number"
                          min="1"
                          value={it.qty}
                          onChange={(ev) => patchItem(it.id, { qty: parseInt(ev.target.value, 10) || 1 })}
                        />
                      </div>
                      {items.length > 1 && (
                        <div className="slot-window-del" onClick={() => setItems((prev) => prev.filter((x) => x.id !== it.id))}>
                          ×
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <span className="add-window-link" onClick={() => setItems((prev) => [...prev, newItem()])}>
                  + Add Another Item
                </span>
              </Field>
            ) : (
              <Field label={kind === 'open' ? 'Dates' : 'Dates & Time Windows'}>
                {kind === 'open' && (
                  <div className="repeat-row">
                    <div className="tp-field">
                      <label>Repeat</label>
                      <select value={repeatEvery} onChange={(e) => setRepeatEvery(e.target.value)}>
                        <option value="7">Every week</option>
                        <option value="14">Every 2 weeks</option>
                        <option value="28">Every 4 weeks</option>
                      </select>
                    </div>
                    <div className="tp-field">
                      <label>How many</label>
                      <input
                        type="number"
                        className="num"
                        min="2"
                        max="30"
                        value={repeatCount}
                        onChange={(e) => setRepeatCount(e.target.value)}
                      />
                    </div>
                    <button type="button" className="go" onClick={generateRepeatDates}>Generate dates</button>
                    <div className="why">
                      Uses the first date above as the start, and copies its spots and label to the rest. Anything already
                      listed is replaced.
                    </div>
                  </div>
                )}
                <div>
                  {dates.map((d) => (
                    <div className="slot-date-block" key={d.id}>
                      <div className="slot-date-head">
                        <input type="date" value={d.date} onChange={(e) => patchDate(d.id, { date: e.target.value })} />
                        {dates.length > 1 && (
                          <span
                            className="remove-date-link"
                            onClick={() => setDates((prev) => prev.filter((x) => x.id !== d.id))}
                          >
                            Remove Date
                          </span>
                        )}
                      </div>
                      {d.windows.map((w) => {
                        /* digits only; blank = no limit (capacity null) */
                        const capField = (
                          <div className="tp-field cap-field">
                            <label>Spots</label>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={w.capacity ?? ''}
                              placeholder="No limit"
                              onChange={(ev) => {
                                const v = ev.target.value.replace(/\D/g, '');
                                patchWindow(d.id, w.id, { capacity: v ? Math.max(1, parseInt(v, 10)) : null });
                              }}
                            />
                          </div>
                        );
                        /* By date: no time pickers — what varies per date is an optional label */
                        if (kind === 'open') {
                          return (
                            <div className="slot-window-row" key={w.id}>
                              <div className="tp-field" style={{ flex: 1, minWidth: 0 }}>
                                <label>
                                  What’s this date for{' '}
                                  <span style={{ textTransform: 'none', fontWeight: 500 }}>(optional)</span>
                                </label>
                                <input
                                  type="text"
                                  className="date-label-input"
                                  value={d.label}
                                  placeholder="e.g. Teacher lunch"
                                  onChange={(ev) => patchDate(d.id, { label: ev.target.value })}
                                />
                              </div>
                              {capField}
                            </div>
                          );
                        }
                        const s = splitTime(w.start);
                        const e = splitTime(w.end);
                        return (
                          <div className="slot-window-row" key={w.id}>
                            <div className="tp-field">
                              <label>Start</label>
                              <div className="time-picker">
                                <select value={s.h} onChange={(ev) => patchWindow(d.id, w.id, { start: joinTime(ev.target.value, s.m) })}>
                                  {HOUR_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                  ))}
                                </select>
                                <select value={s.m} onChange={(ev) => patchWindow(d.id, w.id, { start: joinTime(s.h, ev.target.value) })}>
                                  {MINUTE_OPTIONS.map((m) => (
                                    <option key={m} value={m}>{m}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="tp-field">
                              <label>End</label>
                              <div className="time-picker">
                                <select value={e.h} onChange={(ev) => patchWindow(d.id, w.id, { end: joinTime(ev.target.value, e.m) })}>
                                  {HOUR_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                  ))}
                                </select>
                                <select value={e.m} onChange={(ev) => patchWindow(d.id, w.id, { end: joinTime(e.h, ev.target.value) })}>
                                  {MINUTE_OPTIONS.map((m) => (
                                    <option key={m} value={m}>{m}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            {capField}
                            {d.windows.length > 1 && (
                              <div
                                className="slot-window-del"
                                onClick={() =>
                                  setDates((prev) =>
                                    prev.map((x) =>
                                      x.id === d.id ? { ...x, windows: x.windows.filter((y) => y.id !== w.id) } : x
                                    )
                                  )
                                }
                              >
                                ×
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {kind === 'slots' && (
                        <span
                          className="add-window-link"
                          onClick={() =>
                            setDates((prev) =>
                              prev.map((x) =>
                                x.id === d.id ? { ...x, windows: [...x.windows, newWindow('12:00', '13:00')] } : x
                              )
                            )
                          }
                        >
                          + Add another time window
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <span className="add-window-link" onClick={() => setDates((prev) => [...prev, newDateBlock(kind)])}>
                  + Add Another Date
                </span>
              </Field>
            )}
          </div>
          <div>
            <Field label="Preview" />
            <div className="signup-preview-card">
              {image && <img className="banner" src={image} alt="" />}
              <div className="t">{title.trim() || 'Sign-Up Title'}</div>
              <div className="d">{desc.trim() || 'Add a description so parents know what to expect.'}</div>
              <div>
                {kind === 'items' ? (
                  previewItems.length ? (
                    previewItems.map((i) => (
                      <div className="signup-slot-row" key={i.id}>
                        <div className="info">
                          <div className="dt">{i.name.trim()}</div>
                        </div>
                        <span className="cap">{i.qty} needed</span>
                        <span className="su-btn">Claim</span>
                      </div>
                    ))
                  ) : (
                    <div className="hint">Add an item to see the list parents will claim from.</div>
                  )
                ) : previewDates.length ? (
                  previewDates.map((d) =>
                    d.windows.map((w) => {
                      const sub = slotSubLabel(d, w);
                      return (
                        <div className="signup-slot-row" key={`${d.id}-${w.id}`}>
                          <div className="info">
                            <div className="dt">{fmtDate(d.date)}</div>
                            {sub && <div className="tm">{sub}</div>}
                          </div>
                          {/* the same wording a parent gets, with nobody signed up yet */}
                          <span className="cap">{spotsLabel({ capacity: w.capacity, signed: [] })}</span>
                          <span className="su-btn">Sign Up</span>
                        </div>
                      );
                    })
                  )
                ) : (
                  <div className="hint">Add a date to see what parents will sign up for here.</div>
                )}
                {acceptsDonations && (
                  <div className="signup-slot-row" style={{ justifyContent: 'center' }}>
                    <span className="su-btn" style={{ background: 'var(--amber-bg)', color: 'var(--amber)' }}>
                      💳 Can't volunteer? Donate instead
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="inline-form-actions">
          <button className="btn btn-ghost" disabled={saving} onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={save}>
            {saving ? (
              <><Spinner /> Saving…</>
            ) : editingSignup ? 'Save Changes' : 'Save Sign-Up'}
          </button>
        </div>
      </Card>
    </>
  );
}
